// How a Needs-attention row reads (ART-324). The SERVER decides which rows exist, their kind and their order
// (`NeedsAttentionService`); this module is the one place a row becomes words, shared by every lens and both
// scopes so "Hold by Dana Whitfield (Harbor Light Mgmt)" cannot be phrased two ways on two pages.
import type { components } from "@cntrl/api-client";
import { oppTypeLabel } from "../dashboard/oppDisplay.js";
import { formatDay, offerDaysLeft } from "./rosterFormat.js";

export type AttentionItem = components["schemas"]["AttentionItem"];

/** Timing turns red at this many days or fewer (product owner, ART-324). */
export const URGENT_DAYS = 3;

/** The kind tag. An opportunity says its own type ("PR · Placement"), never the generic word. */
export function attentionKindLabel(item: AttentionItem): string {
  switch (item.kind) {
    case "hold": return "Hold";
    case "show": return "Show";
    case "settle": return "Settle";
    case "release": return "Release";
    case "content": return "Content";
    case "rehearsal": return "Rehearsal";
    case "request": return "Request";
    case "ready": return "Ready";
    case "agent": return "Agent";
    case "opportunity":
      return item.opportunityType
        ? oppTypeLabel({ type: item.opportunityType, prSubtype: item.opportunitySubtype })
        : "Opp";
    default: return "Item";
  }
}

/** The headline: the thing, plus its day where the day is part of what it is ("Exit/In · Nashville · Oct 29",
 *  "Paper Anchors · out Oct 30"). An untitled opportunity is named by its type. */
export function attentionTitle(item: AttentionItem): string {
  const base = item.title?.trim() || (item.kind === "opportunity" || item.kind === "content" || item.kind === "rehearsal"
    ? attentionKindLabel(item)
    : "Untitled");
  const day = item.date ? formatDay(item.date) : "";
  if (!day) return base;
  if (item.kind === "release") return `${base} · out ${day}`;
  if (item.kind === "hold" || item.kind === "show" || item.kind === "settle" || item.kind === "request"
      || item.kind === "ready" || item.kind === "agent") return `${base} · ${day}`;
  return base;
}

/** "Dana Whitfield (Harbor Light Mgmt)" — the company, or "band" for someone on the band's own seats; just the
 *  name when they no longer reach the artist. */
export function attentionWho(source: AttentionItem["source"]): string | null {
  if (!source?.name) return null;
  const org = source.org ?? (source.team === "artist" ? "band" : null);
  return org ? `${source.name} (${org})` : source.name;
}

/** The source line — who put it here, and for a release how much of its work is still open. */
export function attentionSourceLine(item: AttentionItem): string | null {
  if (item.kind === "settle") return "Settlement not closed out";
  if (item.kind === "ready") return "Team is in · finish the deal with the buyer";
  // ART-330 — the team's view of the same hold: everyone is in, the agent has the green light to confirm.
  if (item.kind === "agent") {
    const agent = item.source?.name?.trim().split(/\s+/)[0];
    return agent ? `Waiting on ${agent} to confirm` : "Waiting on the agent to confirm";
  }
  const who = attentionWho(item.source);
  const lead = !who
    ? null
    : item.kind === "hold" ? `Hold by ${who}`
    : item.kind === "show" ? (item.approved ? `Approved by ${who}` : `Booked by ${who}`)
    : item.kind === "request" ? `Show request from ${who}`
    : item.kind === "release" ? `Release created by ${who}`
    : item.kind === "content" ? `Content day created by ${who}`
    : item.kind === "rehearsal" ? `Rehearsal created by ${who}`
    : `Sent by ${who}`;
  const todos = item.kind === "release" && (item.totalTodos ?? 0) > 0
    ? `${item.openTodos ?? 0} of ${item.totalTodos} to-dos open`
    : null;
  return [lead, todos].filter(Boolean).join(" · ") || null;
}

/** "2 in · 1 waiting · 1 out" over a hold's people; null with nobody on it. */
export function inviteeCountLine(invitees: AttentionItem["invitees"]): string | null {
  const list = invitees ?? [];
  if (list.length === 0) return null;
  const n = (s: string) => list.filter((i) => i.status === s).length;
  const parts = [
    n("in") ? `${n("in")} in` : null,
    n("pending") ? `${n("pending")} waiting` : null,
    n("out") ? `${n("out")} out` : null,
  ];
  return parts.filter(Boolean).join(" · ");
}

/** The right-hand timing and whether it is red. A hold counts down to its EXPIRY; everything else to its own
 *  day; a settlement is "after show". */
export function attentionTiming(item: AttentionItem, now: Date = new Date()): { text: string; urgent: boolean } | null {
  if (item.kind === "settle") return { text: "after show", urgent: false };
  if (item.kind === "ready") return { text: "confirm", urgent: false };
  if (item.kind === "request") {
    // A request counts down to its reply-by — the offer's Good through.
    const reply = offerDaysLeft(item.respondBy);
    if (reply == null) return null;
    if (reply < 0) return { text: "reply overdue", urgent: true };
    return { text: reply === 0 ? "reply today" : `reply in ${reply}d`, urgent: reply <= URGENT_DAYS };
  }
  if (item.kind === "hold" && item.expiresAt) {
    const ms = new Date(item.expiresAt).getTime() - now.getTime();
    if (Number.isNaN(ms)) return null;
    if (ms <= 0) return { text: "expired", urgent: true };
    const days = Math.floor(ms / 86_400_000);
    return { text: days === 0 ? "expires today" : `expires in ${days}d`, urgent: days <= URGENT_DAYS };
  }
  const days = offerDaysLeft(item.date);
  if (days == null) return null;
  if (days < 0) return { text: item.kind === "release" ? "out now" : `${-days}d ago`, urgent: false };
  if (days === 0) return { text: "today", urgent: true };
  return { text: `in ${days}d`, urgent: days <= URGENT_DAYS };
}

export type AttentionPillTone = "red" | "amber" | "neutral" | "green";

/** The short status word a Needs-attention row carries on the phone (ART-358 / ART-361), and its tone. One place, so the two lenses
 *  cannot word the same row two ways. Built on the shared `attentionTiming` rule rather than beside it. */
export function attentionPill(item: AttentionItem, now: Date = new Date()): { text: string; tone: AttentionPillTone } | null {
  const timing = attentionTiming(item, now);
  switch (item.kind) {
    case "hold": {
      if (timing?.urgent) return { text: "Expiring", tone: "red" };
      const waiting = (item.invitees ?? []).some((p) => p.status === "pending");
      return waiting ? { text: "Waiting", tone: "amber" } : timing ? { text: timing.text, tone: "neutral" } : null;
    }
    case "request":
      return { text: "Reply", tone: timing?.urgent ? "red" : "amber" };
    case "agent":
      return { text: "Waiting", tone: "amber" };
    case "ready":
      return { text: "Confirm", tone: "green" };
    case "settle":
      return { text: "Settle", tone: "neutral" };
    default:
      if (!timing) return null;
      return { text: timing.text, tone: timing.urgent ? "red" : "neutral" };
  }
}
